import React, { useEffect, useMemo, useState } from 'react';
import userAdminService, {
  type UserStatus,
  type UserAdminCreateRequest,
  type UserAdminDTO,
  type UserAdminUpdateRequest,
} from '../services/userAdminService';
import UserFormModal from '../components/UserFormModal';
import DashboardPagination from '../components/DashboardPagination';
import { UI_THEME } from '../ui/theme';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';
import { buttonClassName } from '../components/ui/buttonStyles';
import Button from '../components/ui/Button';
import { EmptyState, LoadingState } from '../components/ui/Feedback';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';

const maskDocument = (doc: string) => {
  const digits = (doc ?? '').replace(/\D/g, '');

  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
  }

  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.***.***/**${digits.slice(10, 12)}-${digits.slice(12)}`;
  }

  return '***';
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'ATIVO':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-800';
    case 'INATIVO':
      return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600';
    case 'BLOQUEADO':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/50 dark:text-red-200 dark:border-red-800';
    case 'PENDENTE':
      return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600';
  }
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserAdminDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<UserAdminDTO | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });
  const inputClass = FIELD_CLASS;

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await userAdminService.list();
      data.sort((a, b) => (a.nomeCompleto || '').localeCompare(b.nomeCompleto || '') || (a.email || '').localeCompare(b.email || ''));
      setUsers(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter === 'GESTOR_PARCERIA') {
        if (!u.gestorParceria) return false;
      } else if (roleFilter === 'OPERADOR_SECRETARIA') {
        if ((u.role || '').toUpperCase() !== 'OPERADOR' || !u.operadorSecretaria) return false;
      } else if (roleFilter && (u.role || '').toUpperCase() !== roleFilter) {
        return false;
      }
      if (!q) return true;
      const documento = (u.documento ?? '').toLowerCase();
      return (
        (u.nomeCompleto ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.secretaria ?? '').toLowerCase().includes(q) ||
        documento.includes(q) ||
        maskDocument(documento).toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const summaryCards = [
    { label: 'Total exibido', value: filtered.length, dot: 'bg-slate-400' },
    { label: 'Usuários cadastrados', value: users.length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Ativos', value: users.filter((u) => u.status === 'ATIVO').length, dot: 'bg-emerald-500' },
    { label: 'Pendentes', value: users.filter((u) => u.status === 'PENDENTE').length, dot: 'bg-amber-500' },
  ];

  const openCreate = () => {
    setModalMode('create');
    setSelected(null);
    setModalOpen(true);
  };

  const openEdit = (u: UserAdminDTO) => {
    setModalMode('edit');
    setSelected(u);
    setModalOpen(true);
  };

  const onCreate = async (payload: UserAdminCreateRequest) => {
    await userAdminService.create(payload);
    setMessage({ type: 'success', text: 'Usuário criado com sucesso' });
    await load();
  };

  const onUpdate = async (id: number, payload: UserAdminUpdateRequest) => {
    await userAdminService.update(id, payload);
    setMessage({ type: 'success', text: 'Usuário atualizado com sucesso' });
    await load();
  };

  const onToggleStatus = async (u: UserAdminDTO) => {
    const newStatus: UserStatus = u.status === 'ATIVO' ? 'INATIVO' : 'ATIVO';
    const action = newStatus === 'INATIVO' ? 'desabilitar' : 'habilitar';
    const ok = window.confirm(`Deseja ${action} o usuário "${u.nomeCompleto}"?`);
    if (!ok) return;

    try {
      await userAdminService.update(u.id, { status: newStatus });
      setMessage({ type: 'success', text: `Usuário ${action} com sucesso` });
      await load();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || `Erro ao ${action} usuário` });
      console.error(e);
    }
  };

  const onDelete = async (u: UserAdminDTO) => {
    const ok = window.confirm(`Deseja excluir o usuário "${u.nomeCompleto}"?`);
    if (!ok) return;

    try {
      await userAdminService.remove(u.id);
      setMessage({ type: 'success', text: 'Usuário excluído com sucesso' });
      await load();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Erro ao excluir usuário' });
      console.error(e);
    }
  };

  const roleLabel = (role?: string) => {
    switch ((role || '').toUpperCase()) {
      case 'ADMIN': return 'Admin';
      case 'GESTOR_JURIDICO': return 'Gestor Jurídico';
      // COMISSAO_SELECAO removido
      case 'JURIDICO': return 'Procuradoria Geral Municipal';
      case 'JURIDICO_AUXILIAR': return 'Auxiliar da Procuradoria (PGM)';
      case 'ANALISTA': return 'Analista';
      case 'ORCAMENTO': return 'Orçamento';
      case 'SECRETARIA': return 'Secretaria';
      case 'CONVENIOS': return 'Convênios';
      case 'OPERADOR': return 'Operador';
      case 'CAMARA': return 'Câmara Municipal';
      case 'VEREADOR': return 'Parlamentar';
      default: return role || 'Operador';
    }
  };

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Acessos' : 'Administrador'}
            </p>
            <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Usuários</h1>
            <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
              {isSaas
                ? 'Gerencie perfis, vínculos e permissões de acesso da equipe.'
                : 'Cadastro, perfis e controle de acesso ao sistema.'}
            </p>
          </div>

          <button
            type="button"
            onClick={openCreate}
            className={`shadow-sm ${primaryButtonClass}`}
          >
            Novo usuário
          </button>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-3xl border p-4 ${surfaceClass}`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail ou documento..."
            className={`${inputClass} md:max-w-sm`}
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={`${FIELD_BASE_CLASS} md:w-56`}
          >
            <option value="">Todos os perfis</option>
            <option value="ADMIN">Admin</option>
            <option value="GESTOR_PARCERIA">Gestor da Parceria</option>
            <option value="GESTOR_JURIDICO">Gestor Jurídico</option>
            <option value="JURIDICO">Procuradoria (PGM)</option>
            <option value="JURIDICO_AUXILIAR">Auxiliar PGM</option>
            <option value="ANALISTA">Analista</option>
            <option value="ORCAMENTO">Orçamento</option>
            <option value="SECRETARIA">Secretaria</option>
            <option value="CONVENIOS">Convênios</option>
            <option value="OPERADOR">Operador</option>
            <option value="OPERADOR_SECRETARIA">Operador de secretaria</option>
            <option value="CAMARA">Câmara Municipal</option>
            <option value="VEREADOR">Parlamentar</option>
          </select>
          <button
            type="button"
            onClick={load}
            className={`${secondaryButtonClass}`}
          >
            Recarregar
          </button>

          <div className={`text-sm md:ml-auto ${bodyTextClass}`}>
            Exibindo <span className={`font-semibold ${strongTextClass}`}>{paginatedUsers.length}</span> de <span className={`font-semibold ${strongTextClass}`}>{filtered.length}</span>
          </div>
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">{error}</div>}

      <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState bare title="Nenhum usuário encontrado." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>E-mail</Th>
                  <Th>Documento</Th>
                  <Th>Perfil</Th>
                  <Th>Secretaria</Th>
                  <Th>Situação</Th>
                  <Th align="right">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedUsers.map((u) => (
                  <Tr key={u.id}>
                    <Td>
                      <div className={`font-medium ${strongTextClass}`}>{u.nomeCompleto}</div>
                      {u.cargoFuncao && <div className={`text-xs ${labelTextClass}`}>{u.cargoFuncao}</div>}
                    </Td>
                    <Td>{u.email}</Td>
                    <Td className={`font-mono ${bodyTextClass}`}>{maskDocument(u.documento || '')}</Td>
                    <Td>
                      {roleLabel(u.role)}
                      {(u.role ?? '').toUpperCase() === 'OPERADOR' && u.operadorSecretaria && (
                        <span className="ml-1.5 inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300">
                          Secretaria
                        </span>
                      )}
                      {u.gestorParceria && (
                        <span className="ml-1.5 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                          Gestor da Parceria
                        </span>
                      )}
                    </Td>
                    <Td>
                      {u.secretaria || ''}
                    </Td>
                    <Td>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadge(u.status)}`}>
                        {u.status}
                      </span>
                    </Td>
                    <Td align="right">
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(u)}
                          className={`${secondaryButtonClass}`}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleStatus(u)}
                          className={`${secondaryButtonClass}`}
                        >
                          {u.status === 'ATIVO' ? 'Inativar' : 'Ativar'}
                        </button>
                        <Button variant="danger-outline" onClick={() => onDelete(u)}>
                          Excluir
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <DashboardPagination
              page={currentPage}
              pageSize={pageSize}
              totalItems={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(1);
              }}
              itemLabel="usuários"
            />
          </div>
        )}
      </section>

      <UserFormModal
        open={modalOpen}
        mode={modalMode}
        user={selected}
        onClose={() => setModalOpen(false)}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      {message && (
        <div className={`fixed bottom-4 right-4 mb-4 mr-4 rounded px-4 py-3 text-sm shadow-md ${
          message.type === 'success'
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200'
            : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default UsersPage;
